import React from 'react';
import {
  TableColumn,
  ColumnFilters,
  Departments,
  TableDateRangeFilter,
  OnDateRangeFilter,
  ResetDateFilter,
  ThemedLink,
  ColoredCircle,
  Authorize,
} from '@erecruit/modules';
import { Intl } from '@erecruit/core';
import { SubType } from '@erecruit/ui-kit/src/utils/tsHelpers';
import {
  Resources,
  Requests,
  Mutations,
  AppPermissions,
} from '@erecruit/types';
import { Flex, Text, styled, Tag, TagProps } from '@erecruit/ui-kit';
import moment from 'moment';
import ColumnActions from './ColumnActions';

const { FormattedMessage, FormattedDate } = Intl;

const messages = messageIds.vms.shiftSearch;

type SearchParamsKey = keyof SubType<
  Resources.ShiftSearchFilter,
  number[] | string[]
>;

export type ShiftColumnType = TableColumn<
  Resources.ShiftSearch,
  number | string,
  Resources.ShiftSearchFilter,
  number[] | string[]
>;

const filterTransform = (values: string[]) => values.map(Number);

type ColumnKeys = {
  shiftId: string;
  candidate: string;
  description: string;
  startDate: string;
  shiftStatus: string;
  facilityDepartments: string;
  shiftType: string;
  shiftTime: string;
  actions: string;
};

const columnKeys: ColumnKeys = {
  shiftId: nameof<Resources.ShiftSearch>(o => o.id),
  description: nameof<Resources.ShiftSearch>(o => o.description),
  startDate: nameof<Resources.ShiftSearch>(o => o.start),
  shiftStatus: nameof.full<Resources.ShiftSearch>(r => r.shiftStatus.name),
  candidate: nameof.full<Resources.ShiftSearch>(o => o.candidate.name),
  facilityDepartments: nameof<Resources.ShiftSearch>(
    r => r.facilityDepartments,
  ),
  shiftType: nameof.full<Resources.ShiftSearch>(o => o.shiftType.name),
  shiftTime: 'shiftTime',
  actions: 'actions',
};

const StyledTag = styled(Tag)<TagProps>(({ theme: { newColors } }) => ({
  backgroundColor: 'transparent',
  border: `1px solid ${newColors.borderInputPrimary}`,
}));

export const defaultVisibleColumns = [
  columnKeys.shiftId,
  columnKeys.candidate,
  columnKeys.description,
  columnKeys.shiftTime,
  columnKeys.startDate,
  columnKeys.shiftStatus,
  columnKeys.facilityDepartments,
  columnKeys.actions,
];

type ParseTableFilters = (
  filters: Partial<Record<keyof Resources.ShiftSearch, string[]>>,
  columns: ShiftColumnType[],
) => Resources.ShiftSearchFilter;

export const parseTableFilters: ParseTableFilters = (filters, columns) => {
  const filter: Resources.ShiftSearchFilter = {};
  Object.keys(filters).forEach(key => {
    const filterColumnKey = key as keyof Resources.ShiftSearch;
    const column = columns.find(col => col.key === key);

    if (column?.searchParamsKey && column?.filterTransform) {
      const { filterTransform, searchParamsKey } = column;
      searchParamsKey === 'createdByIds'
        ? (filter.createdByIds = filterTransform(
            filters[filterColumnKey] || [],
          ) as string[])
        : (filter[searchParamsKey] = filterTransform(
            filters[filterColumnKey] || [],
          ) as number[]);
    } else {
      console.warn(`Table filter ${key} applied but can't parsed.`);
    }
  });
  return filter;
};

export const getDefinedShiftReportColumns = (
  intl: Intl.InjectedIntl,
): Mutations.ColumnDefinition[] => [
  {
    id: nameof<Resources.ShiftSearch>(o => o.id),
    header: intl.formatMessage({ id: messages.shiftId }),
  },
  {
    id: nameof<Resources.ShiftSearch>(o => o.candidate),
    header: intl.formatMessage({ id: messages.candidate }),
  },
  {
    id: nameof<Resources.ShiftSearch>(o => o.description),
    header: intl.formatMessage({ id: messages.description }),
  },
  {
    id: nameof<Resources.ShiftSearch>(o => o.start),
    header: intl.formatMessage({ id: messages.start }),
  },
  {
    id: nameof<Resources.ShiftSearch>(o => o.end),
    header: intl.formatMessage({ id: messages.end }),
  },
  {
    id: nameof<Resources.ShiftSearch>(o => o.shiftStatus),
    header: intl.formatMessage({ id: messages.shiftStatus }),
  },
  {
    id: nameof<Resources.ShiftSearch>(o => o.facilityDepartments),
    header: intl.formatMessage({ id: messages.facilityDepartment }),
  },
  {
    id: nameof<Resources.ShiftSearch>(o => o.position),
    header: intl.formatMessage({ id: messages.position }),
  },
  {
    id: nameof<Resources.ShiftSearch>(o => o.shiftType),
    header: intl.formatMessage({ id: messages.shiftType }),
  },
  {
    id: nameof<Resources.ShiftSearch>(o => o.createdOn),
    header: intl.formatMessage({ id: messages.createdOn }),
  },
  {
    id: nameof<Resources.ShiftSearch>(o => o.createdBy),
    header: intl.formatMessage({ id: messages.createdBy }),
  },
  {
    id: nameof<Resources.ShiftSearch>(o => o.worksite),
    header: intl.formatMessage({ id: messages.worksite }),
  },
  {
    id: nameof<Resources.ShiftSearch>(o => o.cancellationReason),
    header: intl.formatMessage({ id: messages.cancellationReason }),
  },
];

type GenerateColumns = (
  filterByDate: OnDateRangeFilter,
  resetDateFilter: ResetDateFilter,
  filterState: Resources.ShiftSearchFilter,
) => ShiftColumnType[];

export const generateColumns: GenerateColumns = (
  filterByDate,
  resetDateFilter,
  filterState,
) => {
  const columns: ShiftColumnType[] = [
    {
      title: <FormattedMessage id={messages.shiftId} />,
      key: columnKeys.shiftId,
      dataIndex: columnKeys.shiftId,
      sorter: true,
      render: (value: string, { id }: Resources.ShiftSearch) => (
        <Authorize
          permission={`${AppPermissions.ViewShifts} & ${AppPermissions.ViewShift}`}
        >
          {(isAuthorized: boolean) =>
            isAuthorized ? (
              <ThemedLink to={`/shifts/${id}`}>{id}</ThemedLink>
            ) : (
              <Text>{id}</Text>
            )
          }
        </Authorize>
      ),
      fixed: 'left',
    },
    {
      title: <FormattedMessage id={messages.candidate} />,
      key: columnKeys.candidate,
      dataIndex: nameof.full<Resources.ShiftSearch>(o => o.candidate.name),
      sorter: true,
      render: (name: string, record: Resources.ShiftSearch) => {
        if (!record.candidate) {
          return (
            <StyledTag color="bgInputPrimary">
              <Text color="bodySecondary">
                <FormattedMessage id={messages.noCandidateMessage} />
              </Text>
            </StyledTag>
          );
        }
        return <Text>{name}</Text>;
      },
    },
    {
      title: <FormattedMessage id={messages.description} />,
      dataIndex: nameof<Resources.ShiftSearch>(o => o.description),
      key: nameof<Resources.ShiftSearch>(o => o.description),
      sorter: true,
      render: description => (
        <Flex style={{ maxWidth: 250 }}>
          <Text>{description || '-'}</Text>
        </Flex>
      ),
    },
    {
      title: <FormattedMessage id={messages.shiftTime} />,
      key: columnKeys.shiftTime,
      render: (value, { start, end }: Resources.ShiftSearch) => {
        const startTime = moment(start).format('hh:mm A');
        const endTime = moment(end).format('hh:mm A');
        return (
          <Text>
            {startTime} - {endTime}
          </Text>
        );
      },
    },
    {
      title: <FormattedMessage id={messages.startDate} />,
      key: columnKeys.startDate,
      dataIndex: nameof.full<Resources.ShiftSearch>(r => r.start),
      sorter: true,
      render: (value, { start }: Resources.ShiftSearch) => (
        <FormattedDate value={start} />
      ),
    },
    {
      title: <FormattedMessage id={messages.shiftStatus} />,
      key: columnKeys.shiftStatus,
      dataIndex: nameof.full<Resources.ShiftSearch>(r => r.shiftStatus.name),
      filteredValue: filterState.shiftStatusIds || [],
      sorter: true,
      searchParamsKey: nameof<Resources.ShiftSearchFilter>(
        o => o.shiftStatusIds,
      ) as keyof SearchParamsKey,
      filterDropdown: props => (
        <ColumnFilters {...props} request={Requests.ShiftStatusRequest} />
      ),
      filterTransform,
      render: (
        shiftStatusName: string,
        { shiftStatus }: Resources.ShiftSearch,
      ) => {
        return shiftStatus ? (
          <Flex gutter={2} align="center">
            <ColoredCircle color={shiftStatus?.color} size={8} />
            <Text>{shiftStatusName}</Text>
          </Flex>
        ) : (
          <Text>-</Text>
        );
      },
    },
    {
      title: <FormattedMessage id={messages.facilityDepartment} />,
      key: columnKeys.facilityDepartments,
      filteredValue: filterState.facilityDepartmentIds || [],
      searchParamsKey: nameof<Resources.ShiftSearchFilter>(
        o => o.facilityDepartmentIds,
      ) as keyof SearchParamsKey,
      filterDropdown: props => (
        <ColumnFilters
          {...props}
          request={Requests.FacilityDepartmentRequest}
        />
      ),
      filterTransform,
      render: ({
        facilityDepartments,
      }: {
        facilityDepartments: Resources.ShiftSearch['facilityDepartments'];
      }) => {
        return <Departments departments={facilityDepartments} />;
      },
    },
    {
      title: <FormattedMessage id={messages.position} />,
      key: nameof.full<Resources.ShiftSearch>(o => o.position.name),
      dataIndex: nameof.full<Resources.ShiftSearch>(o => o.position.name),
      sorter: true,
      searchParamsKey: nameof<Resources.ShiftSearchFilter>(
        o => o.positionIds,
      ) as keyof SearchParamsKey,
      filteredValue: filterState.positionIds || [],
      filterDropdown: props => (
        <ColumnFilters {...props} request={Requests.PositionRequest} />
      ),
      filterTransform,
      render: (value, { position, company }: Resources.ShiftSearch) => {
        return (
          <Authorize
            permission={`${AppPermissions.ViewPositions} & ${AppPermissions.ViewPosition}`}
          >
            {(isAuthorized: boolean) =>
              isAuthorized ? (
                <>
                  <ThemedLink to={`/shifts/position/${position?.id}`}>
                    {`${position?.name} (${position?.id})`}
                  </ThemedLink>
                  <Text>{company?.name}</Text>
                </>
              ) : (
                <Text>
                  <Text>{`${position?.name} (${position?.id})`}</Text>
                  <Text>{company?.name}</Text>
                </Text>
              )
            }
          </Authorize>
        );
      },
    },
    {
      title: <FormattedMessage id={messages.shiftType} />,
      key: columnKeys.shiftType,
      dataIndex: nameof.full<Resources.ShiftSearch>(o => o.shiftType.name),
      filteredValue: filterState.shiftTypeIds || [],
      sorter: true,
      searchParamsKey: nameof<Resources.ShiftSearchFilter>(
        o => o.shiftTypeIds,
      ) as keyof SearchParamsKey,
      filterDropdown: props => (
        <ColumnFilters {...props} request={Requests.ShiftTypeRequest} />
      ),
      filterTransform,
      render: (shiftTypeName: string) => (
        <Text>{shiftTypeName ? shiftTypeName : '-'}</Text>
      ),
    },
    {
      title: <FormattedMessage id={messages.createdOn} />,
      dataIndex: nameof<Resources.ShiftSearch>(o => o.createdOn),
      key: nameof<Resources.ShiftSearch>(o => o.createdOn),
      sorter: true,
      filteredValue: filterState.createdOn
        ? [filterState.createdOn.from, filterState.createdOn.to]
        : [],
      render: value => (value ? <FormattedDate value={value} /> : null),
      filterDropdown: ({ confirm }) => (
        <TableDateRangeFilter
          dateFieldKey={nameof<Resources.ShiftSearch>(o => o.createdOn)}
          range={filterState.createdOn}
          resetFilter={(dateFieldKey: string) => {
            confirm && confirm();
            resetDateFilter(dateFieldKey);
          }}
          onFilter={(dateFieldKey, dateRange) => {
            confirm && confirm();
            filterByDate(dateFieldKey, dateRange);
          }}
        />
      ),
    },
    {
      title: <FormattedMessage id={messages.createdBy} />,
      key: nameof.full<Resources.ShiftSearch>(o => o.createdBy.name),
      dataIndex: nameof.full<Resources.ShiftSearch>(o => o.createdBy.name),
      sorter: true,
      searchParamsKey: nameof<Resources.ShiftSearchFilter>(
        o => o.createdByIds,
      ) as keyof SearchParamsKey,
      filteredValue: filterState.createdByIds || [],
      filterDropdown: props => {
        return (
          <ColumnFilters
            {...props}
            request={Requests.ShiftSearchCreatedByRequest}
          />
        );
      },
      // createdBy is filtered by guid param and we dont want to convert it into number
      filterTransform: values => values,
      render: (name: string) => {
        return <Text>{name || '-'}</Text>;
      },
    },
    {
      title: <FormattedMessage id={messages.worksite} />,
      dataIndex: nameof.full<Resources.ShiftSearch>(o => o.worksite.name),
      key: nameof.full<Resources.ShiftSearch>(o => o.worksite.name),
      sorter: true,
      searchParamsKey: nameof<Resources.ShiftSearchFilter>(
        o => o.worksiteIds,
      ) as keyof SearchParamsKey,
      filteredValue: filterState.worksiteIds || [],
      filterDropdown: props => (
        <ColumnFilters {...props} request={Requests.WorksiteRequest} />
      ),
      filterTransform,
    },
    {
      title: <FormattedMessage id={messages.cancellationReason} />,
      key: nameof.full<Resources.ShiftSearch>(o => o.cancellationReason.name),
      dataIndex: nameof.full<Resources.ShiftSearch>(
        o => o.cancellationReason.name,
      ),
      sorter: true,
      searchParamsKey: nameof<Resources.ShiftSearchFilter>(
        o => o.cancellationReasonIds,
      ) as keyof SearchParamsKey,
      filteredValue: filterState.cancellationReasonIds || [],
      filterDropdown: props => {
        return (
          <ColumnFilters
            {...props}
            request={Requests.ShiftCancellationReasonRequest}
          />
        );
      },
      filterTransform,
      render: (name: string) => {
        return <Text color="bodySecondary">{name || '-'}</Text>;
      },
    },
    {
      key: columnKeys.actions,
      render: (props: Resources.ShiftSearch) => {
        return <ColumnActions shiftSearch={props} filterState={filterState} />;
      },
      width: 15,
      fixed: 'right',
    },
  ];

  return columns;
};
